/**
 * @fileoverview Tracking API Client (Contract-First Design)
 * @module api-client/clients/tracking
 *
 * 100% type-safe Tracking API client generated from contract.
 * All methods, types, and validation come from TRACKING_API_CONTRACT.
 */

import {
	TRACKING_API_CONTRACT,
	type StartTrackingPayload,
	type StartTrackingResponse,
	type TrackEventPayload,
	type TrackPageviewPayload,
	type TrackEventApiResponse,
	type TrackEventsBatchPayload,
	type TrackEventsBatchApiResponse,
	type ListEventsQuery,
	type TrackingAggregatesQuery,
} from '@monorepo/types';
import { ContractClient } from '../core/contract-client';
import type { HttpClient } from '../core/http-client';

/**
 * Tracking API Client
 *
 * Contract-first client for analytics tracking operations.
 * All methods are type-safe and validated against the API contract.
 *
 * This client handles:
 * - Tracking initialization
 * - Event tracking (single and batch)
 * - Pageview tracking
 * - Event retrieval
 *
 * @example
 * ```typescript
 * const trackingClient = new TrackingClient(httpClient);
 *
 * // Start tracking
 * const tracking = await trackingClient.start({
 *   pageUrl: window.location.href,
 *   referrer: document.referrer,
 *   utmSource: 'google',
 *   utmMedium: 'cpc'
 * });
 *
 * // Track an event
 * await trackingClient.trackEvent({
 *   eventType: 'cta_clicked',
 *   eventData: { buttonId: 'start-test', location: 'hero' },
 *   pageUrl: window.location.href
 * });
 *
 * // Track a pageview
 * await trackingClient.trackPageview({
 *   pageUrl: window.location.href,
 *   pageTitle: document.title,
 *   referrer: document.referrer
 * });
 *
 * // Batch track events
 * await trackingClient.trackEventsBatch({
 *   events: [
 *     { eventType: 'test_started', eventData: { testId: '123' } },
 *     { eventType: 'test_step_completed', eventData: { step: 1 } }
 *   ]
 * });
 *
 * // Get events
 * const events = await trackingClient.getEvents();
 * ```
 */
export class TrackingClient extends ContractClient<typeof TRACKING_API_CONTRACT> {
	constructor(httpClient: HttpClient) {
		super(httpClient, TRACKING_API_CONTRACT, {
			validateResponses: true,
			validateRequests: true,
			throwOnValidationError: true,
		});
	}

	/**
	 * Start tracking for a new visitor
	 * POST /tracking/start
	 *
	 * Initializes tracking for a new visitor and returns a tracking ID.
	 * This should be called once when the user first visits the site.
	 */
	async start(payload: StartTrackingPayload): Promise<StartTrackingResponse> {
		return this.execute('start', { body: payload });
	}

	/**
	 * Track a single event
	 * POST /tracking/event
	 *
	 * Records a single tracking event (e.g., button click, form submission).
	 * Events are associated with the current session via SessionGuard.
	 */
	async trackEvent(payload: TrackEventPayload): Promise<TrackEventApiResponse> {
		return this.execute('trackEvent', { body: payload });
	}

	/**
	 * Track multiple events in a single request
	 * POST /tracking/events/batch
	 *
	 * Efficiently tracks multiple events in a single API call.
	 * Useful for offline event queuing or bulk event submission.
	 */
	async trackEventsBatch(payload: TrackEventsBatchPayload): Promise<TrackEventsBatchApiResponse> {
		return this.execute('trackEventsBatch', { body: payload });
	}

	/**
	 * Track a pageview
	 * POST /tracking/pageview
	 *
	 * Records a pageview event with URL, title, and referrer information.
	 * Should be called on every route change in SPA applications.
	 */
	async trackPageview(payload: TrackPageviewPayload): Promise<TrackEventApiResponse> {
		return this.execute('trackPageview', { body: payload });
	}

	/**
	 * ADMIN METHODS (Contract-First)
	 */

	/**
	 * List all tracking events (Admin only)
	 * GET /tracking/admin/list
	 *
	 * Retrieves all tracking events with pagination and filtering.
	 * Supports filtering by userId, sessionId, and eventType.
	 * Requires admin authentication.
	 */
	async list(query?: Partial<ListEventsQuery>) {
		return this.execute('adminListEvents', { query });
	}

	/**
	 * Get tracking event aggregates (Admin only)
	 * GET /tracking/admin/aggregates
	 *
	 * Returns total events, unique sessions, event type breakdown, error rate, and top pages.
	 * Requires admin authentication.
	 */
	async getAggregates(query?: Partial<TrackingAggregatesQuery>) {
		return this.execute('adminEventAggregates', { query });
	}
}
